/*
 * SubscriptionPlansPage — Classic 4-tier plan selector.
 *
 * Route: /plans
 * Access: authenticated users only (wraps in ProtectedRoute in App.js).
 *
 * • Monthly / Yearly toggle (yearly saves 15-22%).
 * • GOLD suggested (highlighted "Recommended" badge).
 * • Back-arrow returns to whichever page opened this (uses location.state.from
 *   or falls back to history.back()).
 * • Selecting a plan hits POST /api/stripe/checkout/subscription and redirects
 *   to Stripe Checkout.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Check, Crown, Sparkles, Zap, Gem, Loader2, TrendingUp, Coins,
} from "lucide-react";
import { authAxios } from "../utils/auth";
import { toast } from "sonner";
import CoinPurchaseModal from "../components/CoinPurchaseModal";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";
import NotificationBell from "../components/notifications/NotificationBell";

const GOLD = "#C5A55A";

const PLAN_META = {
  BRONZE: {
    icon: Zap,
    color: "#cd7f32",
    features: [
      "12 relationship profiles",
      "Increased bonuses on tasks & streaks",
    ],
  },
  SILVER: {
    icon: Sparkles,
    color: "#c0c0c0",
    features: [
      "18 relationship profiles",
      "Higher bonuses on tasks & streaks",
    ],
  },
  GOLD: {
    icon: Crown,
    color: GOLD,
    recommended: true,
    features: [
      "36 relationship profiles",
      "Higher bonuses on tasks & streaks",
    ],
  },
  PLATINUM: {
    icon: Gem,
    color: "#e5e4e2",
    features: [
      "Unlimited relationships",
      "Maximum bonuses on tasks & streaks",
    ],
  },
};

export default function SubscriptionPlansPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [tab, setTab] = useState(searchParams.get("tab") === "topup" ? "topup" : "subscribe");
  const [coinBalance, setCoinBalance] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState("monthly");
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [currentPlan, setCurrentPlan] = useState({ tier: "FREE", cycle: null });
  const [sponsorOffer, setSponsorOffer] = useState(null);
  // Prorated-upgrade preview cache — keyed by target tier (yearly-only).
  // Populated on first render when a user with an active sub views the page.
  const [upgradePreviews, setUpgradePreviews] = useState({});
  const [upgradingTier, setUpgradingTier] = useState(null);

  // Where to go back to — prefer explicit state.from, else browser
  // referrer if it's a same-origin app path, else /dashboard. We
  // normalise document.referrer to a pathname so cross-origin referrers
  // (e.g. Stripe checkout) don't leak into navigate() calls.
  const normalisedReferrer = (() => {
    try {
      if (!document.referrer) return "";
      const u = new URL(document.referrer);
      if (u.origin !== window.location.origin) return "";
      // Never bounce back to /plans itself, /payment/*, or the auth flow.
      const p = u.pathname + u.search;
      if (
        p.startsWith("/plans") ||
        p.startsWith("/payment/") ||
        p.startsWith("/auth") ||
        p === "/"
      ) return "";
      return p;
    } catch { return ""; }
  })();
  const returnTo = location.state?.from || normalisedReferrer || "/dashboard";
  const TIER_RANK = { BRONZE: 1, SILVER: 2, GOLD: 3, PLATINUM: 4 };

  // Remember the caller of /plans so PaymentSuccess CONTINUE returns
  // the user to the page they were on when they opened /plans (e.g.
  // Dashboard or Profile) — not back to /plans itself.
  useEffect(() => {
    try {
      sessionStorage.setItem("agent_eros_payment_return_to", returnTo);
    } catch (_) { /* private mode / storage disabled */ }
  }, [returnTo]);

  // Fetch coin balance for the Top-up hero display
  useEffect(() => {
    authAxios.get("/credits/balance")
      .then((r) => setCoinBalance(r?.data?.balance ?? null))
      .catch(() => setCoinBalance(null));
  }, []);

  // Sync tab → URL so /plans?tab=topup deep-linking works.
  // Preserve `location.state` (including `from`) — a plain `navigate(target,
  // { replace: true })` would wipe state, causing the Subscribe tab's back
  // arrow to fall through to the browser referrer or /dashboard instead of
  // returning to the exact /eroscoach match+session that opened the page.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (tab === "topup") p.set("tab", "topup"); else p.delete("tab");
    const next = p.toString();
    const target = `${location.pathname}${next ? `?${next}` : ""}`;
    if (target !== `${location.pathname}${location.search}`) {
      navigate(target, { replace: true, state: location.state });
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Promise.all([
      authAxios.get("/stripe/plans"),
      authAxios.get("/auth/me").catch(() => null),
      authAxios.get("/affiliate/sponsor-subscribe-offer").catch(() => null),
    ])
      .then(async ([plansRes, meRes, offerRes]) => {
        setPlans(plansRes.data?.plans || []);
        const tier = (meRes?.data?.subscription_tier || "FREE").toUpperCase();
        const bc = meRes?.data?.subscription_billing_cycle || null;
        setCurrentPlan({ tier, cycle: bc });
        if (bc === "yearly" || bc === "monthly") setCycle(bc);
        setSponsorOffer(offerRes?.data?.offer || null);

        // If user has an active subscription, prefetch yearly upgrade previews
        // for every strictly-higher tier so the plan cards can show the
        // "credit applied" price without an extra click.
        if (tier !== "FREE") {
          const higher = (plansRes.data?.plans || [])
            .filter((p) => TIER_RANK[p.tier] > TIER_RANK[tier]);
          const results = await Promise.all(
            higher.map((p) =>
              authAxios
                .get(`/stripe/subscription/upgrade-preview?tier=${p.tier}`)
                .then((r) => [p.tier, r.data])
                .catch(() => [p.tier, null])
            )
          );
          const map = {};
          for (const [t, d] of results) if (d) map[t] = d;
          setUpgradePreviews(map);
        }
      })
      .catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBack = () => {
    try {
      if (typeof returnTo === "string" && returnTo.startsWith("/")) {
        navigate(returnTo);
      } else if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate("/dashboard");
      }
    } catch {
      navigate("/dashboard");
    }
  };

  const handleSelect = async (tier) => {
    setCheckoutTier(tier);
    try {
      const { data } = await authAxios.post("/stripe/checkout/subscription", {
        tier,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Checkout failed");
        setCheckoutTier(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setCheckoutTier(null);
    }
  };

  const handleUpgradeYearly = async (tier) => {
    const preview = upgradePreviews[tier];
    if (!preview) return;
    const ok = window.confirm(
      `Upgrade to Yearly ${tier}?\n\n` +
      `New plan cost: $${preview.new_plan_cost_usd}\n` +
      `Credit for unused time: −$${preview.credit_usd}\n` +
      `Total charged today: $${preview.total_due_usd}\n\n` +
      `Your payment method on file will be charged immediately.`
    );
    if (!ok) return;
    setUpgradingTier(tier);
    try {
      const { data } = await authAxios.post("/stripe/subscription/upgrade", { tier });
      if (data?.upgraded) {
        toast.success(`Upgraded to Yearly ${tier} — $${data.total_due_usd} charged`);
        // Reload to refresh currentPlan + coin balances.
        setTimeout(() => window.location.reload(), 900);
      } else {
        toast.error("Upgrade failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upgrade failed");
    } finally {
      setUpgradingTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="subscription-plans-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0a0a0f]/85 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-white/60 hover:text-white transition"
              data-testid="plans-back-btn"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Back</span>
            </button>
            <h1 className="flex items-center gap-2 font-cinzel text-xl text-white truncate">
              <Coins className="w-5 h-5 text-amber-400 flex-shrink-0" />
              Coin Shop
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <CoinBalancePill />
            <NotificationBell />
            <AdminMenu />
            <MainMenu exclude="/plans" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="text-center mb-8 sm:mb-12">
          <p className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Top up your balance or subscribe to unlock deeper bonuses, more relationship profiles, and richer streak & task rewards.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8 border-b border-white/10" data-testid="plans-tabs">
          <button
            onClick={() => setTab("topup")}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              tab === "topup" ? "border-[#C5A55A] text-[#C5A55A]" : "border-transparent text-white/50 hover:text-white/80"
            }`}
            data-testid="tab-topup"
          >
            <Coins className="w-4 h-4" /> Top-up
          </button>
          <button
            onClick={() => setTab("subscribe")}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              tab === "subscribe" ? "border-[#C5A55A] text-[#C5A55A]" : "border-transparent text-white/50 hover:text-white/80"
            }`}
            data-testid="tab-subscribe"
          >
            <Crown className="w-4 h-4" /> Subscribe
          </button>
        </div>

        {tab === "topup" ? (
          <CoinPurchaseModal
            embedded
            currentBalance={coinBalance}
            onPurchaseComplete={(nb) => setCoinBalance(nb)}
          />
        ) : (
        <>
        {/* Sponsor Subscribe Bonus Offer banner */}
        {sponsorOffer && (
          <div
            className="mb-6 rounded-xl border border-[#C5A55A]/50 bg-gradient-to-r from-[#C5A55A]/[0.12] to-[#C5A55A]/[0.03] p-4 sm:p-5"
            data-testid="sponsor-subscribe-offer-banner"
          >
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 mt-0.5 shrink-0" style={{ color: GOLD }} />
              <div className="flex-1">
                <div className="text-sm sm:text-base text-white">
                  <span className="font-semibold" style={{ color: GOLD }}>Limited Offer from {sponsorOffer.sponsor_name}</span> —
                  Subscribe today and receive up to{" "}
                  <span className="font-semibold text-white">
                    {(cycle === "yearly"
                      ? sponsorOffer.base_bonus
                      : Math.floor(sponsorOffer.base_bonus / 2)
                    ).toLocaleString()}
                  </span>{" "}
                  bonus coins.
                </div>
                <div className="text-xs text-white/60 mt-1">
                  {cycle === "yearly"
                    ? "Yearly Plans unlock maximum bonuses and savings — bonus scaled by plan."
                    : "Switch to Yearly for maximum bonus payouts and savings."}
                  {!sponsorOffer.unlimited && (
                    <> · <span className="text-[#C5A55A] font-semibold">{sponsorOffer.uses_remaining}</span> offers remaining</>
                  )}
                </div>
                {sponsorOffer.welcome_message && (
                  <blockquote
                    className="mt-3 border-l-2 pl-3 italic text-white/80 text-sm sm:text-[15px] leading-relaxed"
                    style={{ borderColor: GOLD }}
                    data-testid="sponsor-subscribe-offer-welcome"
                  >
                    <span aria-hidden="true" style={{ color: GOLD }}>“</span>
                    {sponsorOffer.welcome_message}
                    <span aria-hidden="true" style={{ color: GOLD }}>”</span>
                  </blockquote>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-full border border-white/10 p-1 bg-white/[0.03]">
            {[
              { k: "monthly", label: "Monthly" },
              { k: "yearly", label: "Yearly" },
            ].map((c) => (
              <button
                key={c.k}
                onClick={() => setCycle(c.k)}
                data-testid={`cycle-${c.k}`}
                className={`px-5 py-2 text-sm rounded-full transition-all ${
                  cycle === c.k
                    ? "bg-[#C5A55A] text-black font-medium"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {c.label}
                {c.k === "yearly" && (
                  <span className={`ml-2 text-[10px] font-medium ${cycle === "yearly" ? "text-emerald-900" : "text-emerald-400"}`}>
                    Save up to 33%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plans grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((p) => {
              const meta = PLAN_META[p.tier] || {};
              const Icon = meta.icon || Sparkles;
              const price = cycle === "yearly" ? p.yearly_usd : p.monthly_usd;
              const monthly = cycle === "yearly" ? p.monthly_usd : null;
              const isRec = !!meta.recommended;
              const busy = checkoutTier === p.tier;
              const isCurrentTier = currentPlan.tier === p.tier;
              const isCurrentPlanExact = isCurrentTier && currentPlan.cycle === cycle;
              // Cycle-aware bonus: yearly Platinum = the sponsor's cap; monthly = half.
              const cycleBonus = sponsorOffer?.plan_bonuses?.[cycle]?.[p.tier] || 0;
              // Prorated upgrade eligibility — only when viewer already has a paid
              // sub AND this card is a strictly-higher tier AND cycle=yearly.
              const canUpgrade =
                currentPlan.tier !== "FREE" &&
                cycle === "yearly" &&
                TIER_RANK[p.tier] > TIER_RANK[currentPlan.tier];
              const preview = canUpgrade ? upgradePreviews[p.tier] : null;
              const upgrading = upgradingTier === p.tier;
              return (
                <div
                  key={p.tier}
                  data-testid={`plan-card-${p.tier}`}
                  className={`relative rounded-2xl border p-6 flex flex-col transition-all ${
                    isCurrentTier
                      ? "border-emerald-400/60 bg-gradient-to-b from-emerald-400/[0.05] to-transparent"
                      : isRec
                      ? "border-[#C5A55A]/70 bg-gradient-to-b from-[#C5A55A]/[0.06] to-transparent shadow-[0_0_60px_-15px_rgba(197,165,90,0.35)]"
                      : "border-white/[0.08] bg-[#111113] hover:border-white/20"
                  }`}
                >
                  {isCurrentTier && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-400 text-black text-[10px] font-semibold tracking-wider">
                      CURRENT PLAN
                    </div>
                  )}
                  {!isCurrentTier && isRec && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#C5A55A] text-black text-[10px] font-semibold tracking-wider">
                      RECOMMENDED
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5" style={{ color: meta.color || GOLD }} />
                    <h3 className="text-lg font-semibold tracking-tight" style={{ color: meta.color || GOLD }}>
                      {p.tier}
                    </h3>
                  </div>
                  {p.coins_per_month > 0 && (
                    <p className="text-xs text-white/50 mb-4 min-h-[32px]" data-testid={`plan-coins-${p.tier}`}>
                      {p.coins_per_month.toLocaleString()} coins / month
                      {cycleBonus > 0 && (
                        <>
                          <br />
                          <span className="text-[#C5A55A] font-medium" data-testid={`plan-bonus-${p.tier}`}>
                            +{cycleBonus.toLocaleString()} bonus · Total{" "}
                            {(p.coins_per_month + cycleBonus).toLocaleString()} first month
                          </span>
                        </>
                      )}
                    </p>
                  )}
                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white tracking-tight">${price}</span>
                      <span className="text-sm text-white/40">
                        /{cycle === "yearly" ? "yr" : "mo"}
                      </span>
                    </div>
                    {cycle === "yearly" && p.yearly_savings_usd > 0 && (
                      <div className="text-[11px] text-emerald-400 mt-1">
                        Save ${p.yearly_savings_usd} vs monthly (${monthly}/mo)
                      </div>
                    )}
                    {/* Prorated upgrade breakdown — only shown for higher-tier yearly cards */}
                    {preview && preview.credit_usd > 0 && (
                      <div
                        className="mt-2 text-[11px] text-[#C5A55A] leading-relaxed"
                        data-testid={`plan-upgrade-preview-${p.tier}`}
                      >
                        Upgrade credit: −${preview.credit_usd} (unused {(preview.current_cycle || "monthly")} time)
                        <br />
                        <span className="text-white/80 font-medium">Total due today: ${preview.total_due_usd}</span>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-2 mb-6 flex-1">
                    {(meta.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-white/70">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: meta.color || GOLD }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isCurrentPlanExact ? (
                    <button
                      disabled
                      data-testid={`plan-current-${p.tier}`}
                      className="w-full py-2.5 rounded-lg text-sm font-medium bg-emerald-400/15 text-emerald-300 border border-emerald-400/40 cursor-default"
                    >
                      Current Plan · {cycle === "yearly" ? "Yearly" : "Monthly"}
                    </button>
                  ) : canUpgrade && preview ? (
                    <button
                      onClick={() => handleUpgradeYearly(p.tier)}
                      disabled={upgrading || !!upgradingTier}
                      data-testid={`plan-upgrade-${p.tier}`}
                      className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isRec
                          ? "bg-[#C5A55A] hover:bg-[#d4b872] text-black"
                          : "bg-emerald-500 hover:bg-emerald-400 text-black"
                      } ${upgrading ? "opacity-70 cursor-wait" : ""}`}
                    >
                      {upgrading ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Upgrading…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> Upgrade for ${preview.total_due_usd}
                        </span>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSelect(p.tier)}
                      disabled={busy || !!checkoutTier}
                      data-testid={`plan-select-${p.tier}`}
                      className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isCurrentTier
                          ? "bg-emerald-500 hover:bg-emerald-400 text-black"
                          : isRec
                          ? "bg-[#C5A55A] hover:bg-[#d4b872] text-black"
                          : "bg-white/[0.08] hover:bg-white/[0.14] text-white border border-white/10"
                      } ${busy ? "opacity-70 cursor-wait" : ""}`}
                    >
                      {busy ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                        </span>
                      ) : isCurrentTier ? (
                        `Switch to ${cycle === "yearly" ? "Yearly" : "Monthly"}`
                      ) : (
                        `Choose ${p.tier}`
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Cancel-anytime note — reassures cost-averse users right below the grid */}
        <p
          className="text-center text-xs text-white/50 mt-6"
          data-testid="plans-cancel-note"
        >
          You can cancel a subscription plan at any time and it will not be renewed.
        </p>
        </>
        )}
      </main>
    </div>
  );
}
